import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { commissionsApi, payPeriodsApi, formatDate, formatDateRange } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  CheckCircle2, 
  XCircle, 
  Clock,
  TrendingUp,
  Pencil,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SalesDashboard = () => {
  const { user, effectiveUser, impersonating } = useAuth();
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commissionToEdit, setCommissionToEdit] = useState(null);
  const [commissionToDelete, setCommissionToDelete] = useState(null);
  
  // Form state
  const [date, setDate] = useState(new Date());
  const [clientName, setClientName] = useState('');
  const [productType, setProductType] = useState('brickai_membership');
  const [saleAmount, setSaleAmount] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [periodRes, commissionsRes] = await Promise.all([
        payPeriodsApi.getCurrent(),
        commissionsApi.getAll()
      ]);
      setCurrentPeriod(periodRes.data);
      setCommissions(commissionsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await commissionsApi.create({
        date: format(date, 'yyyy-MM-dd'),
        client_name: clientName,
        product_type: productType,
        sale_amount: parseFloat(saleAmount),
        description
      });
      
      toast.success('Commission logged successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to log commission';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!commissionToEdit) return;
    setSubmitting(true);

    try {
      await commissionsApi.update(commissionToEdit.id, {
        client_name: clientName,
        product_type: productType,
        sale_amount: parseFloat(saleAmount),
        description
      });
      
      toast.success('Commission updated');
      setEditDialogOpen(false);
      setCommissionToEdit(null);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update commission';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!commissionToDelete) return;
    
    try {
      await commissionsApi.delete(commissionToDelete.id);
      toast.success('Commission deleted');
      setDeleteDialogOpen(false);
      setCommissionToDelete(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete commission';
      toast.error(message);
    }
  };

  const openEditDialog = (commission) => {
    setCommissionToEdit(commission);
    setClientName(commission.client_name);
    setProductType(commission.product_type);
    setSaleAmount(commission.sale_amount.toString());
    setDescription(commission.description || '');
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (commission) => {
    setCommissionToDelete(commission);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setDate(new Date());
    setClientName('');
    setProductType('brickai_membership');
    setSaleAmount('');
    setDescription('');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs">
            <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
            <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
            Pending
          </Badge>
        );
    }
  };

  const getProductLabel = (type) => {
    return type === 'brickai_membership' ? 'BrickAI Membership' : 'Website';
  };

  // Current period commissions
  const currentPeriodCommissions = commissions.filter(
    c => currentPeriod && c.pay_period_start === currentPeriod.start_date
  );

  // Calculate stats
  const totalSales = currentPeriodCommissions.reduce((sum, c) => sum + c.sale_amount, 0);
  const totalCommission = currentPeriodCommissions.reduce((sum, c) => sum + c.commission_amount, 0);
  const approvedCommission = currentPeriodCommissions
    .filter(c => c.status === 'approved')
    .reduce((sum, c) => sum + c.commission_amount, 0);
  const pendingCommission = currentPeriodCommissions
    .filter(c => c.status === 'pending')
    .reduce((sum, c) => sum + c.commission_amount, 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="sales-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Sales Dashboard
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {currentPeriod && (
                <>
                  Current Period: <span className="font-medium text-brick-cyan">{formatDateRange(currentPeriod.start_date, currentPeriod.end_date)}</span>
                </>
              )}
            </p>
          </div>
          
          {!impersonating && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brick-red hover:bg-[#c4151d] font-medium" data-testid="log-commission-btn">
                  <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                  Log Commission
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">Log New Commission</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start border-slate-300"
                          data-testid="commission-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-brick-cyan" aria-hidden="true" />
                          {format(date, 'PPP')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => d && setDate(d)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client Name</Label>
                    <Input
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="e.g., Acme Corp"
                      required
                      data-testid="client-name-input"
                      className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Product Type</Label>
                    <Select value={productType} onValueChange={setProductType}>
                      <SelectTrigger data-testid="product-type-select" className="border-slate-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="brickai_membership">BrickAI Membership (10% commission)</SelectItem>
                        <SelectItem value="website">Website (5% commission)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sale Amount ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={saleAmount}
                      onChange={(e) => setSaleAmount(e.target.value)}
                      placeholder="0.00"
                      required
                      data-testid="sale-amount-input"
                      className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                    />
                    {saleAmount && (
                      <p className="text-sm text-green-600 font-medium">
                        Commission: ${(parseFloat(saleAmount) * (productType === 'brickai_membership' ? 0.10 : 0.05)).toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description (optional)</Label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add notes about this sale..."
                      rows={3}
                      data-testid="commission-description-input"
                      className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-brick-red hover:bg-[#c4151d] font-medium"
                    disabled={submitting}
                    data-testid="submit-commission-btn"
                  >
                    {submitting ? 'Logging...' : 'Log Commission'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Sales</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-sales">
                ${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Commission</p>
              <p className="text-2xl font-bold text-green-600 mt-1" data-testid="total-commission">
                ${totalCommission.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Approved</p>
              <p className="text-2xl font-bold text-green-600 mt-1" data-testid="approved-commission">
                ${approvedCommission.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending</p>
              <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="pending-commission">
                ${pendingCommission.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Commissions */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg">Recent Commissions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {currentPeriodCommissions.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-300" aria-hidden="true" />
                <p className="text-slate-500">No commissions logged this period</p>
                {!impersonating && (
                  <Button 
                    variant="outline" 
                    className="mt-4 border-brick-cyan text-brick-cyan hover:bg-brick-cyan hover:text-white"
                    onClick={() => setDialogOpen(true)}
                    data-testid="empty-state-log-btn"
                  >
                    Log Your First Commission
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {currentPeriodCommissions.slice(0, 10).map((commission) => (
                  <div
                    key={commission.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`commission-${commission.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-sm text-brick-cyan">
                          {formatDate(commission.date)}
                        </span>
                        {getStatusBadge(commission.status)}
                        <Badge variant="outline" className="text-xs border-slate-300">
                          {getProductLabel(commission.product_type)}
                        </Badge>
                      </div>
                      <p className="font-medium text-slate-900 truncate">{commission.client_name}</p>
                      {commission.description && (
                        <p className="text-sm text-slate-500 line-clamp-1">{commission.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Sale: ${commission.sale_amount.toLocaleString()}</p>
                        <p className="font-mono text-lg font-bold text-green-600">
                          +${commission.commission_amount.toFixed(2)}
                        </p>
                      </div>
                      {commission.status === 'pending' && !impersonating && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(commission)}
                            className="hover:bg-brick-cyan/10"
                            data-testid={`edit-commission-${commission.id}`}
                          >
                            <Pencil className="w-4 h-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(commission)}
                            className="hover:bg-red-50"
                            data-testid={`delete-commission-${commission.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" aria-hidden="true" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Edit Commission</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client Name</Label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  required
                  className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Product Type</Label>
                <Select value={productType} onValueChange={setProductType}>
                  <SelectTrigger className="border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brickai_membership">BrickAI Membership (10%)</SelectItem>
                    <SelectItem value="website">Website (5%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sale Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={saleAmount}
                  onChange={(e) => setSaleAmount(e.target.value)}
                  required
                  className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-300">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brick-red hover:bg-[#c4151d]" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Delete Commission</DialogTitle>
            </DialogHeader>
            <p className="text-slate-500">
              Are you sure you want to delete this commission for <strong className="text-slate-900">{commissionToDelete?.client_name}</strong>?
            </p>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SalesDashboard;
